import React, { useEffect, useRef } from 'react';
import { AlertTriangle, AlertCircle, Info, X, Shield, Trash2, Play, StopCircle } from 'lucide-react';
import './ConfirmationModal.css';

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface ConfirmationRequest {
  id: string;
  operation: string;
  tool_name: string;
  risk_level: RiskLevel;
  description: string;
  parameters: Record<string, unknown>;
  timestamp: Date;
}

interface ConfirmationModalProps {
  request: ConfirmationRequest;
  onConfirm: (requestId: string) => void;
  onCancel: (requestId: string) => void;
  isOpen: boolean;
}

/**
 * ConfirmationModal - Displays confirmation dialog for critical operations
 *
 * Shows detailed information about the operation being requested,
 * with visual indicators based on risk level (low, medium, high, critical).
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  request,
  onConfirm,
  onCancel,
  isOpen,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management - trap focus in modal
  useEffect(() => {
    if (isOpen && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel(request.id);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel, request.id]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Get risk level styling
  const getRiskLevelConfig = (level: RiskLevel) => {
    switch (level) {
      case 'critical':
        return {
          icon: AlertTriangle,
          iconClass: 'confirmation-icon-critical',
          badgeClass: 'confirmation-badge-critical',
          label: 'Critical Operation',
          description: 'This action cannot be undone and may affect production data.',
        };
      case 'high':
        return {
          icon: AlertCircle,
          iconClass: 'confirmation-icon-high',
          badgeClass: 'confirmation-badge-high',
          label: 'High Risk',
          description: 'This action may have significant impact on your data.',
        };
      case 'medium':
        return {
          icon: Shield,
          iconClass: 'confirmation-icon-medium',
          badgeClass: 'confirmation-badge-medium',
          label: 'Medium Risk',
          description: 'Please review the details before proceeding.',
        };
      default:
        return {
          icon: Info,
          iconClass: 'confirmation-icon-low',
          badgeClass: 'confirmation-badge-low',
          label: 'Low Risk',
          description: 'This operation requires your confirmation.',
        };
    }
  };

  // Get operation icon
  const getOperationIcon = (toolName: string) => {
    if (toolName.includes('delete')) return Trash2;
    if (toolName.includes('execute') || toolName.includes('run')) return Play;
    if (toolName.includes('kill') || toolName.includes('stop')) return StopCircle;
    return Shield;
  };

  const riskConfig = getRiskLevelConfig(request.risk_level);
  const RiskIcon = riskConfig.icon;
  const OperationIcon = getOperationIcon(request.tool_name);

  // Format tool name for display
  const formatToolName = (name: string): string => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Format parameter key for display
  const formatParamKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div
      className="confirmation-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel(request.id);
        }
      }}
      role="presentation"
    >
      <div
        ref={modalRef}
        className={`confirmation-modal confirmation-modal-${request.risk_level}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirmation-title"
        aria-describedby="confirmation-description"
      >
        {/* Header */}
        <div className="confirmation-header">
          <div className="confirmation-header-left">
            <div className={`confirmation-icon-wrapper ${riskConfig.iconClass}`}>
              <RiskIcon size={24} />
            </div>
            <div className="confirmation-header-text">
              <h2 id="confirmation-title" className="confirmation-title">
                Confirm {request.operation}
              </h2>
              <span className={`confirmation-badge ${riskConfig.badgeClass}`}>
                {riskConfig.label}
              </span>
            </div>
          </div>
          <button
            className="confirmation-close"
            onClick={() => onCancel(request.id)}
            aria-label="Cancel operation"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="confirmation-body">
          <p id="confirmation-description" className="confirmation-description">
            {request.description || riskConfig.description}
          </p>

          {/* Operation Details */}
          <div className="confirmation-details">
            <div className="confirmation-detail-row">
              <span className="confirmation-detail-label">
                <OperationIcon size={14} />
                Tool
              </span>
              <span className="confirmation-detail-value">
                {formatToolName(request.tool_name)}
              </span>
            </div>

            {/* Parameters */}
            {Object.keys(request.parameters).length > 0 && (
              <div className="confirmation-params">
                <span className="confirmation-params-label">Parameters:</span>
                <div className="confirmation-params-list">
                  {Object.entries(request.parameters).map(([key, value]) => (
                    <div key={key} className="confirmation-param">
                      <span className="confirmation-param-key">
                        {formatParamKey(key)}:
                      </span>
                      <span className="confirmation-param-value">
                        {typeof value === 'object'
                          ? JSON.stringify(value, null, 2)
                          : String(value)
                        }
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Warning for critical operations */}
          {request.risk_level === 'critical' && (
            <div className="confirmation-warning">
              <AlertTriangle size={16} />
              <span>
                This is a critical operation. Please verify all parameters before confirming.
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="confirmation-footer">
          <button
            className="confirmation-btn confirmation-btn-cancel"
            onClick={() => onCancel(request.id)}
          >
            Cancel
          </button>
          <button
            ref={confirmButtonRef}
            className={`confirmation-btn confirmation-btn-confirm confirmation-btn-${request.risk_level}`}
            onClick={() => onConfirm(request.id)}
          >
            {request.risk_level === 'critical' ? 'Confirm & Execute' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
